import { BulkAction } from 'src/app/shared/interfaces'

import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core'
import { DataTable } from '@pascalhonegger/ng-datatable'
import { Sale, SaleFilters, StatusCode } from '../sale'
import { Observable, Subject } from 'rxjs'
import { Dictionary } from '@ngrx/entity'
import { Member } from 'src/app/features/member/member'
import { AppState } from 'src/app/core/core.state'
import { Store } from '@ngrx/store'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { Organization } from 'src/app/features/organization/organization'
import { organizationSelectors } from 'src/app/features/organization/organization.selectors'
import { BsModalService } from 'ngx-bootstrap/modal'
import { Product } from '../../product/product'
import { productSelectors } from '../../product/product.selectors'
import { Course } from '../../course/course'
import { Subscription } from '../../subscription/subscription'
import { courseSelectors } from '../../course/course.selectors'
import { subscriptionSelectors } from '../../subscription/subscription.selectors'
import { courseActions } from '../../course/course.actions'
import { subscriptionActions } from '../../subscription/subscription.actions'
import { getEnumAsArray } from 'src/app/shared/until'
import { SaleItem } from '../sale-item/sale-item'
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { Constants } from '../../../shared/constants'
import { saleActions } from '../sale.actions'
import { ConfigurationService } from '../../../shared/services/configuration.service'
import { formatDate } from '@angular/common'
import { saleSelectors } from '../sale.selectors'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { paymentSelectors } from '../payment/payment.selectors'
import { Payment } from '../payment/payment'
import DEBOUNCED_TIME = Constants.DEBOUNCED_TIME

@UntilDestroy()
@Component({
  selector: 'sale-index-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class SaleIndexTableComponent implements OnInit {
  @Input() isLoading: boolean
  @Input() tableData: Sale[]
  @Input() bulkActions: BulkAction[] = []
  @Input() height: string = 'auto'
  @Output() onBulkAction = new EventEmitter<any>()
  @Output() onClickRow = new EventEmitter<any>()

  @ViewChild('dataTable', { static: true }) dataTable: DataTable

  public memberEntities$: Observable<Dictionary<Member>>

  public filterTerm: string = ''
  public allCheckedToggle: boolean = false
  public checkedIds: Map<number, boolean> = new Map()
  public saleStatusCode = StatusCode
  public creditSales: Sale[]

  public saleStatus = getEnumAsArray(StatusCode)

  public searchTerm$ = new Subject<string>()
  public total$: Observable<number>
  public total: number
  public selectedLanguage$: Observable<string>

  public selectedSaleIds: number[] = []
  public selectedFilters: SaleFilters[] = []

  public filters = [
    SaleFilters.Completed,
    SaleFilters.Pending,
    SaleFilters.Cancelled,
    SaleFilters.NoSuccessfulPayments,
    SaleFilters.SuccessfulPayments,
    SaleFilters.PartialPayments
  ]

  constructor(
    private store: Store<AppState>,
    public modalService: BsModalService,
    public configServices: ConfigurationService
  ) {}

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.store.dispatch(subscriptionActions.fetchMany({}))
    this.store.dispatch(courseActions.fetchMany({}))

    this.searchTerm$
      .pipe(
        distinctUntilChanged(),
        debounceTime(DEBOUNCED_TIME),
        untilDestroyed(this),
        filter((s) => s.length > 2)
      )
      .subscribe((term) => {
        this.store.dispatch(
          saleActions.fetchMany({
            params: RequestQueryBuilder.create({
              join: [
                { field: 'creditSales' },
                { field: 'organization' },
                { field: 'customer' }
              ],
              search: {
                $or: [
                  {
                    id: {
                      [CondOperator.CONTAINS_LOW]: term
                    }
                  },
                  {
                    'customer.firstName': {
                      [CondOperator.CONTAINS_LOW]: term
                    }
                  },
                  {
                    'customer.lastName': {
                      [CondOperator.CONTAINS_LOW]: term
                    }
                  },
                  {
                    'organization.description': {
                      [CondOperator.CONTAINS_LOW]: term
                    }
                  }
                ]
              }
            })
          })
        )
      })

    this.total$ = this.store.select(saleSelectors.selectCount).pipe(
      untilDestroyed(this),
      map((total) => {
        if (!this.total) {
          this.total = total
        }
        return this.total
      })
    )
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.tableData?.currentValue) {
      this.creditSales = changes.tableData?.currentValue.filter(
        (x) => !!x.isCreditOfSaleId
      )
    }
  }

  public onRowClicked(row: SaleItem) {
    this.onClickRow.emit(row)
  }

  public onCheckRow(targetRow: Sale) {
    this.checkedIds.set(
      targetRow.id,
      !Boolean(this.checkedIds.get(targetRow.id))
    )
  }

  public onToggleAllChecked() {
    this.allCheckedToggle = !this.allCheckedToggle

    this.dataTable.data.forEach((row) =>
      this.checkedIds.set(row.id, this.allCheckedToggle)
    )
  }

  public onClickBulkAction(bulkOption: string) {
    const rows: Sale[] = this.dataTable.data.filter((x) =>
      this.checkedIds.get(x.id)
    )

    if (rows.length > 0) {
      this.onBulkAction.emit({ action: bulkOption, items: rows })
      this.allCheckedToggle = false
      this.dataTable.data.forEach((row) => this.checkedIds.set(row.id, false))
    }
  }

  public onSearch(value) {
    this.searchTerm$.next(value)
  }

  public onFilterByClicked(filter: SaleFilters) {
    // keep below immutable to trigger pipe after selection
    this.selectedFilters = this.selectedFilters?.includes(filter)
      ? this.selectedFilters?.filter((x) => x !== filter)
      : [...this.selectedFilters, filter]
  }

  public onFilterSaleClicked(sale: Sale) {
    // keep below immutable to trigger pipe after selection
    this.selectedSaleIds = this.selectedSaleIds?.includes(sale.id)
      ? this.selectedSaleIds?.filter((x) => x !== sale.id)
      : [...this.selectedSaleIds, sale.id]
  }
}
